import { useContext, useMemo } from "react";
import { get, upperFirst } from "lodash";
import { useQuery } from "@apollo/client";
import {
  apolloOptionRacePP,
  getFinishPosition
} from "@tvg/ipp/src/graphql/options.graph";
import RacePerformanceQuery from "@tvg/ipp/src/graphql/queries/racePastPerformance.graphql";
import ApolloContext from "@tvg/utils/apolloContext";
import { RaceInfoMyBets, RaceOdds, RaceTypeCodeEnum } from "@tvg/ts-types/Race";
import type { PastPerformanceRace, RacePastPerformanceProps } from "../types";

const formatResultsIPP = (pastRaces: Array<RaceInfoMyBets>) => {
  const raceIds: Array<string> = [];
  return {
    races: (pastRaces || []).map((race) => {
      const runner = get(race, "results.runners[0]");
      const tempRace: PastPerformanceRace = {
        ...race,
        id: `${race.raceId}`,
        runnerNumber: get(runner, "runnerNumber", ""),
        hasReplays: !!get(race, "video.replayFileName", ""),
        replayFileName: get(race, "video.replayFileName", ""),
        raceTypeCode: get(race, "type.code") as RaceTypeCodeEnum,
        distance: `${get(race, "distance.value", "")}${get(
          race,
          "distance.code",
          ""
        )}`.toLowerCase(),
        finishPosition: getFinishPosition(
          get(runner, "finishPosition"),
          get(runner, "scratched", false)
        ),
        favorite: get(runner, "favorite"),
        scratched: get(runner, "scratched", false),
        odds: get(runner, "currentOdds") as RaceOdds,
        postRaceReport: upperFirst(get(runner, "timeform.postRaceReport", "")),
        accBeatenDistance: upperFirst(
          get(runner, "timeform.accBeatenDistance", "")
        ),
        accBeatenDistanceStatus: upperFirst(
          get(runner, "timeform.accBeatenDistanceStatus", "")
        ),
        accBeatenDistanceStatusAbrev: upperFirst(
          get(runner, "timeform.accBeatenDistanceStatusAbrev", "")
        )
      };

      if (race.raceId) {
        raceIds.push(race.raceId);
      }

      delete tempRace.raceId;
      delete tempRace.results;
      delete tempRace.type;
      delete tempRace.video;

      return tempRace;
    }),
    raceIds
  };
};

const useFetchRacePastPerformance = ({
  trackCode,
  raceNumber,
  raceDate,
  wagerProfile
}: RacePastPerformanceProps) => {
  const { fcpClient } = useContext(ApolloContext);

  const { data, loading } = useQuery(
    RacePerformanceQuery,
    apolloOptionRacePP({
      wagerProfile,
      trackCode,
      raceNumber,
      raceDate,
      fcpClient
    })
  );

  return useMemo(() => {
    if (loading) {
      return null;
    }

    const pastRaces = get(data, "pastRaces", []);
    const resultsPastPerformance = formatResultsIPP(pastRaces);

    return get(resultsPastPerformance, "races[0]", null) as RaceInfoMyBets;
  }, [data, loading]);
};

export default useFetchRacePastPerformance;
